// Çözülmüş metin önbelleği (9.34 · D-610).
//
// NEDEN ZORUNLU: ratchet ile çözme TEK SEFERLİKTİR. Her mesaj zinciri bir adım
// ilerletip anahtarı tüketir; aynı şifreli metni ikinci kez çözmek başarısız
// olur. Sunucu ise yalnız şifreli hâli tutuyor, yani sohbet her yenilendiğinde
// aynı şifreli metin geri gelir. Çözülen metin yerelde saklanmazsa sohbet
// ikinci açılışta okunamaz hâle gelirdi.
//
// İKİNCİ NEDEN, daha az belli olanı: GÖNDEREN kendi giden mesajını ÇÖZEMEZ.
// Gönderme ve alma zincirleri ayrıdır; gönderen tarafta o mesajın anahtarı
// çözmeye değil şifrelemeye yarar. Bu yüzden gönderim anında düz metin
// önbelleğe yazılır — yoksa kişi kendi yazdığını göremezdi.
//
// Düz metin cihazda açık durur; bu, her uçtan uca mesajlaşma uygulamasının
// modeli (yerel veritabanı düz metin tutar, cihaz şifrelemesi korur). Sunucuya
// hiçbir zaman gitmez.
import AsyncStorage from '@react-native-async-storage/async-storage';
import { decryptMessage, SessionEnvelope } from './session';

const cacheKey = (messageId: number, revision = 0): string =>
  `e2ee.plain.${messageId}${revision > 0 ? `:r${revision}` : ''}`;

export type ResolvedContent =
  | { ok: true; text: string }
  | { ok: false; reason: 'undecryptable' };

/** Gönderilen mesajın düz metnini sakla — gönderen onu bir daha çözemez. */
export async function rememberOutgoing(messageId: number, plaintext: string, revision = 0): Promise<void> {
  await AsyncStorage.setItem(cacheKey(messageId, revision), plaintext);
}

/**
 * Şifreli bir mesajın düz metnini ver. Önce önbellek; yoksa bir kez çözülür ve
 * saklanır. Çözülemiyorsa (anahtar tüketilmiş ve önbellek kaybolmuş, ya da zarf
 * bozuk) bu DÜRÜSTÇE bildirilir — arayüz "okunamıyor" gösterebilsin diye.
 */
export async function resolveIncoming(
  peerId: number,
  messageId: number,
  content: string,
  revision = 0,
): Promise<ResolvedContent> {
  const cached = await AsyncStorage.getItem(cacheKey(messageId, revision));
  if (cached !== null) return { ok: true, text: cached };

  let envelope: SessionEnvelope;
  try {
    envelope = JSON.parse(content) as SessionEnvelope;
  } catch {
    return { ok: false, reason: 'undecryptable' };
  }

  try {
    const text = await decryptMessage(peerId, envelope);
    // Çözüldü: bir daha çözülemeyeceği için hemen saklanmalı.
    await AsyncStorage.setItem(cacheKey(messageId, revision), text);
    return { ok: true, text };
  } catch {
    return { ok: false, reason: 'undecryptable' };
  }
}

/** Mesaj silindiğinde/geri alındığında yerel kopyayı da bırakma. */
export async function forgetMessage(messageId: number): Promise<void> {
  await AsyncStorage.removeItem(cacheKey(messageId));
  const revisions = (await AsyncStorage.getAllKeys()).filter(key => key.startsWith(`${cacheKey(messageId)}:r`));
  if (revisions.length) await AsyncStorage.multiRemove(revisions);
}

// Yedeklemenin ihtiyacı olan tek şey bu harita: `mesajKimliği → düz metin`.
// Kim, ne zaman, hangi tür — hepsi zaten sunucuda duruyor ve şifreli değil
// (D-599). Geri yüklerken sunucu satırları verir, yedek de metni; ikisi
// birleşince sohbet okunur hâle döner (9.34 · D-600).
export async function exportPlaintext(): Promise<Record<string, string>> {
  const keys = (await AsyncStorage.getAllKeys()).filter((k) => k.startsWith('e2ee.plain.'));
  const pairs = await AsyncStorage.multiGet(keys);
  const out: Record<string, string> = {};
  for (const [key, value] of pairs) {
    if (value === null) continue;
    out[key.slice('e2ee.plain.'.length)] = value;
  }
  return out;
}

/** Yedekten çözülen metinleri önbelleğe yaz. Var olanın üzerine yazmaz: cihazda
 *  zaten çözülmüş olan, yedekteki eski kopyadan daha güvenilirdir. */
export async function importPlaintext(entries: Record<string, string>): Promise<number> {
  const ids = Object.keys(entries);
  const mevcut = new Set((await AsyncStorage.getAllKeys()).filter((k) => k.startsWith('e2ee.plain.')));
  const yazilacak = ids
    .filter(id => /^[1-9]\d*(?::r[1-9]\d*)?$/.test(id) && typeof entries[id] === 'string')
    .map((id) => [`e2ee.plain.${id}`, entries[id]] as [string, string])
    .filter(([key]) => !mevcut.has(key));
  if (yazilacak.length > 0) await AsyncStorage.multiSet(yazilacak);
  return yazilacak.length;
}
